import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, Wallet, Card, Transaction, Portfolio } from '@/types'
import { mockUser, mockWallets, mockCards, mockTransactions, mockPortfolio } from '@/data/mockData'

interface AppState {
  // User
  user: User | null
  setUser: (user: User | null) => void
  
  // UI State
  balanceHidden: boolean
  toggleBalance: () => void
  
  // Data
  wallets: Wallet[]
  cards: Card[]
  transactions: Transaction[]
  portfolio: Portfolio | null
  
  // Loading states
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  
  // Actions
  addWallet: (wallet: Wallet) => void
  updateWallet: (id: string, updates: Partial<Wallet>) => void
  addCard: (card: Card) => void
  updateCard: (id: string, updates: Partial<Card>) => void
  freezeCard: (id: string) => void
  unfreezeCard: (id: string) => void
  addTransaction: (transaction: Transaction) => void
}

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      // Initial state
      user: mockUser,
      balanceHidden: false,
      wallets: mockWallets,
      cards: mockCards,
      transactions: mockTransactions,
      portfolio: mockPortfolio,
      isLoading: false,
      
      // Actions
      setUser: (user) => set({ user }),
      
      toggleBalance: () => set((state) => ({ 
        balanceHidden: !state.balanceHidden 
      })),
      
      setIsLoading: (loading) => set({ isLoading: loading }),
      
      addWallet: (wallet) => set((state) => ({
        wallets: [...state.wallets, wallet]
      })),
      
      updateWallet: (id, updates) => set((state) => ({
        wallets: state.wallets.map(w => 
          w.id === id ? { ...w, ...updates } : w
        )
      })),
      
      addCard: (card) => set((state) => ({
        cards: [...state.cards, card]
      })),
      
      updateCard: (id, updates) => set((state) => ({
        cards: state.cards.map(c => 
          c.id === id ? { ...c, ...updates } : c
        )
      })),
      
      freezeCard: (id) => set((state) => ({
        cards: state.cards.map(c => 
          c.id === id ? { ...c, status: 'frozen' } : c
        )
      })),
      
      unfreezeCard: (id) => set((state) => ({
        cards: state.cards.map(c => 
          c.id === id ? { ...c, status: 'active' } : c
        )
      })),
      
      addTransaction: (transaction) => set((state) => ({
        transactions: [transaction, ...state.transactions]
      })),
    }),
    {
      name: 'capybank-storage',
      partialize: (state) => ({ 
        balanceHidden: state.balanceHidden,
        user: state.user,
      }),
    }
  )
)

// Derived selectors
export const selectTotalBalance = (state: AppState) => {
  return state.wallets.reduce((sum, w) => sum + w.balance.totalUsd, 0)
}

export const selectDefaultWallet = (state: AppState) => {
  return state.wallets.find(w => w.isDefault) || state.wallets[0]
}

export const selectDefaultCard = (state: AppState) => {
  return state.cards.find(c => c.isDefault) || state.cards[0]
}

export const selectActiveCards = (state: AppState) => {
  return state.cards.filter(c => c.status === 'active')
}

export const selectRecentTransactions = (state: AppState, limit = 5) => {
  return state.transactions.slice(0, limit)
}